/**
 * useFileSources Hook
 * Manage file sources with upload, delete, and view
 */

import { useEffect, useState, useCallback } from 'react';
import adminApi from '@/services/admin.api';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';

/** Rows fetched per page — the list is paginated server-side (page/limit query params). */
const PAGE_SIZE = 20;

export interface FileSourceItem {
  _id: string;
  name: string;
  path: string;
  uniqueName?: string;
  originalName: string;
  mimeType: string;
  size: number;
  s3Url: string;
  createdByName: string;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
}

interface UseFileSourcesResult {
  files: FileSourceItem[];
  loading: boolean;
  uploading: boolean;
  error: string;
  success: string;
  /** Current page, 0-indexed (MUI TablePagination convention). */
  page: number;
  setPage: (page: number) => void;
  /** Total rows across all pages, from the response `meta.total`. */
  total: number;
  PAGE_SIZE: number;
  /** Tenant's plan-specific per-file cap (SubscriptionPlan.features.maxUploadBodySizeMb).
   *  Null until fetched — the backend enforces this regardless, so this is purely for
   *  the upload hint text and an early client-side check to save a round trip. */
  maxUploadSizeMb: number | null;
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (id: string) => Promise<void>;
  getFileDownloadUrl: (id: string) => Promise<string>;
  reload: () => Promise<void>;
  clearError: () => void;
}

// Helper function to extract error message from API response
const extractErrorMessage = (error: any): string => {
  // Check if error has response data with error object
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }

  // Check if error has response data with message
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Check if error has message
  if (error.message) {
    return error.message;
  }

  // Default error message
  return 'An unexpected error occurred';
};

export const useFileSources = (): UseFileSourcesResult => {
  const [files, setFiles] = useState<FileSourceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [maxUploadSizeMb, setMaxUploadSizeMb] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    adminApi
      .getBillingActivePlan()
      .then((res) => setMaxUploadSizeMb(res.activePlan.features.maxUploadBodySizeMb))
      .catch(() => {
        /* non-fatal — the server enforces this regardless; hint/pre-check just won't show */
      });
  }, []);

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data, meta } = await adminApi.getFileSources({ page: page + 1, limit: PAGE_SIZE });
      setFiles(data);
      setTotal(meta?.total ?? 0);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const uploadFiles = async (fileList: File[]) => {
    try {
      setUploading(true);
      setError('');
      setSuccess('');

      // Validate file types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
      ];

      const invalidFiles = fileList.filter((file) => !allowedTypes.includes(file.type));

      if (invalidFiles.length > 0) {
        throw new Error(`Invalid file types. Only PDF, DOC, DOCX, and TXT files are allowed.`);
      }

      // Early client-side check against the tenant's plan-specific cap — saves a
      // round trip on an obvious miss. The backend re-validates this regardless
      // (see file-source.service.ts), so this is a UX nicety, not the source of truth.
      if (maxUploadSizeMb != null) {
        const maxBytes = maxUploadSizeMb * 1024 * 1024;
        const oversizedFiles = fileList.filter((file) => file.size > maxBytes);
        if (oversizedFiles.length > 0) {
          throw new Error(
            `File size exceeds the ${maxUploadSizeMb}MB limit for your plan: ${oversizedFiles
              .map((f) => f.name)
              .join(', ')}`
          );
        }
      }

      // Upload files sequentially
      const uploaded: FileSourceItem[] = [];
      for (const file of fileList) {
        const uploadedFile = await adminApi.uploadFileSource(file);
        uploaded.push(uploadedFile);
      }

      // Server-side pagination: re-read the current page instead of splicing
      // locally, so the page never exceeds PAGE_SIZE rows and `total` stays right.
      await loadFiles();
      setSuccess(`${uploaded.length} file(s) uploaded successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err)) setError(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (id: string) => {
    try {
      setError('');
      await adminApi.deleteFileSource(id);
      // If this was the last row on a non-first page, step back — otherwise the
      // user would land on an empty page. Changing `page` re-runs loadFiles via
      // the effect below, so don't also call it here.
      if (files.length === 1 && page > 0) {
        setPage((prev) => prev - 1);
      } else {
        await loadFiles();
      }
      setSuccess('File deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const getFileDownloadUrl = async (id: string): Promise<string> => {
    try {
      return await adminApi.getFileDownloadUrl(id);
    } catch (err: any) {
      throw new Error(extractErrorMessage(err));
    }
  };

  return {
    files,
    loading,
    uploading,
    error,
    success,
    page,
    setPage,
    total,
    PAGE_SIZE,
    maxUploadSizeMb,
    uploadFiles,
    deleteFile,
    getFileDownloadUrl,
    reload: loadFiles,
    clearError: () => setError(''),
  };
};
